/**
 * The protocol-countries rule, for the browser.
 *
 * A MIRROR of functions/src/utils/protocolCountries.ts
 * (normaliseProtocolCountries, countriesPrefill). The web app cannot import
 * functions code, so the rule is copied here and
 * functions/src/utils/protocolCountries.test.ts fails if the two ever answer
 * differently. Change both together.
 *
 * The names table is the browser's copy in features/consent/privacyPlanKey.ts,
 * which is itself checked against the server's table.
 *
 * Empty means unknown: nothing is prefilled from an empty list. Countries the
 * user entered are never overwritten.
 */
import { COUNTRY_NAMES } from '../features/consent/privacyPlanKey';
import { cleanProtocolField } from './protocolNumberRule';
import { ISO_3166_ALPHA2, ISO_NAME_ALIASES } from './countryCodes';

export interface ProtocolCountries {
  codes: string[];
  unmapped: string[];
}

export interface CountriesPrefill {
  countries?: string[];
  countriesSource?: 'protocol';
  countriesUnmapped?: string[];
}

const rec = (v: unknown): Record<string, unknown> =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
const list = (v: unknown): unknown[] => (Array.isArray(v) ? v : typeof v === "string" ? [v] : []);

/**
 * The lookup key for a NAME: accents removed (NFD, combining marks dropped),
 * apostrophes, commas and parentheses read as spaces, then uppercased with
 * dots, spaces and hyphens removed, the same final form as
 * studyProfile/countryNames.ts. So "Türkiye", "Côte d'Ivoire", "Korea,
 * Republic of" and "European Union (EU)" all look up (2026-10-01).
 */
export const foldCountryKey = (raw: string): string =>
  raw
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/['\u2018\u2019,()]/g, " ")
    .trim()
    .toUpperCase()
    .replace(/[.\s-]+/g, "");

/**
 * Answers that name no country ("NA", "N/A", "None", "Not applicable", "Not
 * stated", "Various", "Worldwide", "Multinational"), keyed like the names
 * table. Ignored in MODEL TEXT only: neither a code nor shown as unmatched
 * (D2). A stored code "NA" is Namibia and never passes through this (D7).
 */
const NON_ANSWERS = new Set(["NA", "N/A", "NONE", "NOTAPPLICABLE", "NOTSTATED", "VARIOUS", "WORLDWIDE", "MULTINATIONAL"]);

/**
 * EU and EEA words, which fill the EU/EEA entry Study Settings offers (D4,
 * decided 2026-10-01). Other regions ("Europe", "Asia-Pacific") are not here
 * and stay unmatched for the user.
 */
const EU_WORDS = new Set([
  "EU", "EEA", "EU/EEA", "EUROPEANUNION", "THEEUROPEANUNION", "EUROPEANUNIONEU",
  "EUROPEANECONOMICAREA", "THEEUROPEANECONOMICAREA", "EUROPEANECONOMICAREAEEA",
]);

/** The shared names table and the ISO names, as folded keys, built once. */
const NAME_KEYS: Record<string, string> = Object.fromEntries([
  ...Object.entries(ISO_3166_ALPHA2).map(([code, name]) => [foldCountryKey(name), code]),
  ...Object.entries(ISO_NAME_ALIASES),
  ...Object.entries(COUNTRY_NAMES),
]);

/** A stored code: an ISO 3166-1 alpha-2 code, or "EU" for the EU/EEA entry. */
export const isCountryCode = (c: string): boolean => c === "EU" || Object.prototype.hasOwnProperty.call(ISO_3166_ALPHA2, c);

const NON_ANSWER = "-";

/**
 * One value of MODEL TEXT as a code, NON_ANSWER for a value that names no
 * country, or "" when it cannot be mapped. A two-letter value is a code only
 * when ISO 3166-1 assigns it ("UK" is GB; "EU" is the EU/EEA entry).
 */
function codeFor(value: string): string {
  const key = foldCountryKey(value);
  if (NON_ANSWERS.has(key)) return NON_ANSWER;
  if (EU_WORDS.has(key)) return "EU";
  if (key === "UK") return "GB";
  if (/^[A-Z]{2}$/.test(key)) return isCountryCode(key) ? key : "";
  return NAME_KEYS[key] ?? "";
}

/**
 * The same lookup as typedCountryCode (below) for NAMES only, with no
 * two-letter reading, used to test joined comma pieces ("Korea, Republic
 * of"). EU/EEA words are EU. "" when the name is not recognised.
 */
export function typedCountryName(entry: string): string {
  const key = foldCountryKey(entry);
  if (!key) return "";
  if (EU_WORDS.has(key)) return "EU";
  return NAME_KEYS[key] ?? "";
}

/**
 * One entry TYPED by the user in the Other countries box (web only; the user
 * path has no functions copy). Names map as on the protocol path ("Japan" is
 * JP, "USA" and "U.S." are US, "UK" is GB, EU/EEA words are EU). Two letters
 * are taken as typed, even when ISO does not assign them: the form warns but
 * never blocks. "" when the entry is not recognised, so the caller can keep
 * it in the box rather than drop it.
 */
export function typedCountryCode(entry: string): string {
  const key = foldCountryKey(entry);
  if (!key) return "";
  if (EU_WORDS.has(key)) return "EU";
  if (key === "UK") return "GB";
  if (/^[A-Z]{2}$/.test(key)) return key;
  return NAME_KEYS[key] ?? "";
}

const pushOnce = (xs: string[], s: string) => {
  if (!xs.some((u) => u.toLowerCase() === s.toLowerCase())) xs.push(s);
};

/** Map MODEL TEXT (an extracted countries list) to codes, keeping what cannot be mapped. */
export function normaliseProtocolCountries(raw: unknown): ProtocolCountries {
  const codes: string[] = [];
  const unmapped: string[] = [];
  for (const v of list(raw)) {
    const s = cleanProtocolField(v);
    if (!s) continue;
    const code = codeFor(s);
    if (code === NON_ANSWER) continue;
    if (code) pushOnce(codes, code);
    else pushOnce(unmapped, s);
  }
  return { codes, unmapped };
}

/**
 * The countries of an analysis. A STORED analysis (`countriesCoded: true`,
 * set by applyProtocolCountries) holds CODES: each is checked against ISO
 * only, with no non-answer filter, so the stored "NA" stays Namibia (D7,
 * 2026-10-01: it used to be read as model text a second time and dropped).
 * An entry that is not a code is kept as unmatched, never dropped. A raw
 * analysis is model text and goes through normaliseProtocolCountries.
 */
function analysisCountries(a: Record<string, unknown>): ProtocolCountries {
  if (a.countriesCoded !== true) {
    return normaliseProtocolCountries([...list(a.countries), ...(Array.isArray(a.countriesUnmapped) ? a.countriesUnmapped : [])]);
  }
  const codes: string[] = [];
  const unmapped: string[] = [];
  for (const v of list(a.countries)) {
    if (typeof v !== "string" || !v.trim()) continue;
    const c = v.trim().toUpperCase();
    if (isCountryCode(c)) pushOnce(codes, c);
    else pushOnce(unmapped, v.trim());
  }
  for (const v of Array.isArray(a.countriesUnmapped) ? a.countriesUnmapped : []) {
    const s = cleanProtocolField(v);
    if (s) pushOnce(unmapped, s);
  }
  return { codes, unmapped };
}

/**
 * Normalise `analysis.countries` in place before the analysis is stored:
 * `countries` becomes the codes, `countriesUnmapped` the values that could
 * not be mapped, and `countriesCoded: true` records that the list is now
 * codes. Idempotent: a stored analysis passes through unchanged.
 */
export function applyProtocolCountries<T extends object>(analysis: T): T {
  const a = analysis as Record<string, unknown>;
  const { codes, unmapped } = analysisCountries(a);
  a.countries = codes;
  a.countriesUnmapped = unmapped;
  a.countriesCoded = true;
  return analysis;
}

/**
 * The Study Settings fields to add from the analysis, or {} when nothing is
 * to be prefilled: Countries already holds an entry (never overwritten), or
 * the analysis gives no countries (unknown, not "none").
 */
export function countriesPrefill(existingSetup: unknown, analysis: unknown): CountriesPrefill {
  const ss = rec(existingSetup);
  const entered = Array.isArray(ss.countries) && ss.countries.some((c) => typeof c === "string" && c.trim() !== "");
  if (entered) return {};
  const { codes, unmapped } = analysisCountries(rec(analysis));
  const out: CountriesPrefill = {};
  if (codes.length) {
    out.countries = codes;
    out.countriesSource = "protocol";
  }
  if (unmapped.length) out.countriesUnmapped = unmapped;
  return out;
}
